import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, BadgeCheck, Clock, Sparkles } from "lucide-react";
import * as React from "react";
import { useEffect, useState } from "react";

import { getTrainingTeamReport, type TrainingTeamReportItem } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { StatCard } from "@/components/StatCard";
import {
  CERTIFICATION_BADGE,
  CERTIFICATION_STATUS_LABEL,
  deriveCertification,
} from "@/components/training/certification";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { SortableHeader } from "@/components/ui/sortable-header";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { type SortValue } from "@/lib/tableSort";
import { useTableSort } from "@/lib/useTableSort";

export interface TeamTrainingDrawerProps {
  /** Open when true; false closes the drawer. */
  open: boolean;
  /** Fired when the drawer requests to close (overlay click / Esc / X). */
  onClose: () => void;
  /** Program Insights (Wave 2): render a leading "Partner" (org name) column.
   * Off by default so the PO Team Knowledge drawer (single-org) is unchanged;
   * the CM/RM/RA program drawer turns it on since its cohort spans many orgs. */
  showPartnerColumn?: boolean;
  /** Override the drawer title/description for the program-wide context.
   * Defaults to the PO "Team Knowledge" copy. */
  title?: string;
  description?: string;
}

/** Ledger filter (CHP-496). The PO drawer defaults to "all" (whole-team picture
 * first); the program drawer defaults to "uncertified" — the cohort that needs
 * chasing across the channel (Wave-2 realignment). */
type CertFilter = "all" | "certified" | "uncertified";
// CHP-836: the certification filter uses the standard white-and-red pill chips
// (TeamDirectory STATUS_CHIPS pattern), not shadcn Tabs — re-review pinned the
// style. Values unchanged; "Uncertified" on both surfaces per CHP-991.
const CERT_FILTER_CHIPS: { value: CertFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "certified", label: "Certified" },
  { value: "uncertified", label: "Uncertified" },
];

const ROLE_LABEL: Record<"po" | "bdm", string> = { po: "PO", bdm: "BDM" };

/** A row counts as certified iff every compulsory module is complete — the same
 * `certified` state the shared three-state vocabulary derives. */
function isCertified(item: TrainingTeamReportItem): boolean {
  return deriveCertification(item) === "certified";
}

/** Sortable columns for the program-mode certification ledger. */
type TrainingSortColumn = "partner" | "name" | "role" | "cert" | "extra";

function makeTrainingSortValue(extraCount: (item: TrainingTeamReportItem) => number) {
  return (item: TrainingTeamReportItem, column: TrainingSortColumn): SortValue => {
    switch (column) {
      case "partner":
        return item.org_name ?? "";
      case "name":
        return item.full_name;
      case "role":
        return ROLE_LABEL[item.role];
      case "cert":
        return CERTIFICATION_STATUS_LABEL[deriveCertification(item)];
      case "extra":
        return extraCount(item);
    }
  };
}

/**
 * Team Knowledge drawer (Insights redesign C2 / CHP-496) — owned by the Training
 * stream lane.
 *
 * The PO Insights "Certification Status" card opens this org-wide
 * certification/development audit (GET /training/reporting/team). Four summary
 * sub-cards segment the team — Fully Certified, Action Required (never started),
 * In Progress (started but unfinished), Proactive Learning (any extra/non-
 * compulsory modules completed) — over a Name / Certification / # Optional
 * modules ledger with an All / Certified / Uncertified filter.
 *
 * Shares the certification.ts vocabulary (`deriveCertification` /
 * `CERTIFICATION_BADGE`) with the inline TeamTrainingReport table so badge
 * wording can never drift; only the surrounding columns/sub-cards differ.
 *
 * Program mode (`showPartnerColumn`, the CM/RM/RA "Channel Partner Knowledge"
 * Insights surface) differs: it defaults to the Non-Certified filter, renders
 * the spec's 4-card % cohort split (Fully Certified / Action Required / In
 * Progress / Proactive Learning, as percentages of the cohort) — CHP review-r2
 * #c02c2843 — adds a Partner + Role (PO/BDM) column, labels the count column
 * "# Optional modules", and makes the ledger sortable. The PO surface (prop
 * off) is unchanged: the 4 PO cohort COUNT cards.
 */
export function TeamTrainingDrawer({
  open,
  onClose,
  showPartnerColumn = false,
  title = "Team Knowledge",
  description = "Manage and track organisation-wide training and development.",
}: TeamTrainingDrawerProps) {
  const { accessToken } = useCurrentUser();
  // Program mode (showPartnerColumn) defaults to the Non-Certified cohort — the
  // people to chase across the channel; the PO drawer keeps its "all" default.
  const defaultFilter: CertFilter = showPartnerColumn ? "uncertified" : "all";
  const [filter, setFilter] = useState<CertFilter>(defaultFilter);

  // Re-seed the filter to its default each time the drawer opens, so reopening
  // doesn't surface a stale last-used filter (the drawer is always mounted).
  useEffect(() => {
    if (open) setFilter(defaultFilter);
  }, [open, defaultFilter]);

  // GET /training/reporting/team is already program-wide for CM/RM/RA (it
  // returns the actor's cohort, which for staff is the whole channel) — so the
  // same fetch backs both the PO and program drawers. The PO key is left
  // exactly as it was; the program drawer caches under a distinct key.
  const { data, isLoading, error } = useQuery<TrainingTeamReportItem[]>({
    queryKey: showPartnerColumn ? ["training", "program-report"] : ["training", "team-report"],
    queryFn: () => getTrainingTeamReport(accessToken!),
    enabled: !!accessToken && open,
  });

  // The "Extra Training" column reads `optional_completions_count` in program
  // mode (Wave-2 wiring) and the PO-legacy `extra_training` otherwise. P1 made
  // the two identical in definition, so the PO drawer is unaffected.
  const extraCount = (item: TrainingTeamReportItem): number =>
    showPartnerColumn ? (item.optional_completions_count ?? 0) : item.extra_training;

  const rows = data ?? [];
  const fullyCertified = rows.filter((item) => deriveCertification(item) === "certified").length;
  const actionRequired = rows.filter(
    (item) => deriveCertification(item) === "never_started",
  ).length;
  const inProgress = rows.filter((item) => deriveCertification(item) === "unfinished").length;
  const proactiveLearning = rows.filter((item) => extraCount(item) > 0).length;
  // Whole-number percentage of the cohort (0 when the cohort is empty, so the
  // cards read "0%" rather than "NaN%"). Used by the program-mode % cards.
  const pct = (n: number): string =>
    rows.length > 0 ? `${Math.round((n / rows.length) * 100)}%` : "0%";

  const visibleRows = rows.filter((item) => {
    if (filter === "all") return true;
    return filter === "certified" ? isCertified(item) : !isCertified(item);
  });

  // CHP-836/839: both the PO Team Knowledge and the program ledger are sortable
  // and read like the platform's standard ledgers. `sortValue` is memoised on
  // `extraCount` (a closure over the mode flag) so the column accessor stays
  // stable per render mode.
  const sortValue = React.useMemo(() => makeTrainingSortValue(extraCount), [showPartnerColumn]); // eslint-disable-line react-hooks/exhaustive-deps
  const { sort, toggle, sortedRows } = useTableSort(visibleRows, sortValue);
  const ledgerRows = sortedRows;
  const colCount = showPartnerColumn ? 5 : 3;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:w-[65vw] sm:max-w-[65vw]"
        data-testid="team-training-drawer"
      >
        <SheetHeader>
          <SheetTitle className="pr-8">{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <p role="status" className="mt-4 text-sm text-muted-foreground">
            Loading training status…
          </p>
        ) : error ? (
          <p role="alert" className="mt-4 text-sm text-destructive">
            Could not load training status.
          </p>
        ) : (
          <>
            {/* CHP-838: both the PO Team Knowledge and the CM/RA/RM Channel
                Partner Knowledge surfaces render the same 4-card cohort split as
                PERCENTAGES of the cohort, using the canonical program-mode
                labels + copy the designer named (supersedes review-r2 c9fab164,
                which set plain counts on the PO surface — designer wins). */}
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                title="Fully Certified"
                value={pct(fullyCertified)}
                subtitle="completed all mandatory requirements"
                tone="success"
                icon={BadgeCheck}
                testId="training-fully-certified"
              />
              <StatCard
                title="Action Required"
                value={pct(actionRequired)}
                subtitle="not started and unable to register deals"
                tone="neutral"
                accent={actionRequired > 0 ? "coral" : undefined}
                icon={AlertTriangle}
                testId="training-action-required"
              />
              <StatCard
                title="In Progress"
                value={pct(inProgress)}
                subtitle="started mandatory training, not finished"
                tone="neutral"
                icon={Clock}
                testId="training-in-progress"
              />
              <StatCard
                title="Proactive Learning"
                value={pct(proactiveLearning)}
                subtitle="completed optional training"
                tone="success"
                icon={Sparkles}
                testId="training-proactive-learning"
              />
            </div>

            <h3 className="mt-6 text-sm font-semibold tracking-tight">Certification ledger</h3>
            {/* CHP-836: left-aligned pill chips (per re-review), replacing the
                right-aligned shadcn tab bar. */}
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="training-filter">
              {CERT_FILTER_CHIPS.map((chip) => (
                <Button
                  key={chip.value}
                  type="button"
                  size="sm"
                  variant={filter === chip.value ? "default" : "outline"}
                  onClick={() => setFilter(chip.value)}
                  data-testid={`training-filter-${chip.value}`}
                >
                  {chip.label}
                </Button>
              ))}
            </div>

            <div className="mt-3">
              <Table>
                <TableHeader>
                  <TableRow>
                    {showPartnerColumn ? (
                      /* Program mode: sortable headers + a Role (PO/BDM) column. */
                      <>
                        {/* CHP-825: "Partner" → "Channel partner" across ledgers. */}
                        <SortableHeader column="partner" sort={sort} onToggle={toggle}>
                          Channel partner
                        </SortableHeader>
                        <SortableHeader column="name" sort={sort} onToggle={toggle}>
                          Name
                        </SortableHeader>
                        <SortableHeader column="role" sort={sort} onToggle={toggle}>
                          Role
                        </SortableHeader>
                        <SortableHeader column="cert" sort={sort} onToggle={toggle}>
                          Certification
                        </SortableHeader>
                        {/* CHP-993/996: the module-count column reads
                            "# Optional modules" on both surfaces. */}
                        <SortableHeader column="extra" sort={sort} onToggle={toggle} align="right">
                          # Optional modules
                        </SortableHeader>
                      </>
                    ) : (
                      /* CHP-836/839: the PO Team Knowledge ledger is sortable +
                         sentence-cased like the platform's standard ledgers. */
                      <>
                        <SortableHeader column="name" sort={sort} onToggle={toggle}>
                          Name
                        </SortableHeader>
                        <SortableHeader column="cert" sort={sort} onToggle={toggle}>
                          Certification
                        </SortableHeader>
                        <SortableHeader column="extra" sort={sort} onToggle={toggle} align="right">
                          # Optional modules
                        </SortableHeader>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledgerRows.map((item) => {
                    const certState = deriveCertification(item);
                    const cert = CERTIFICATION_BADGE[certState];
                    // CHP-840: both surfaces read the canonical cert-status
                    // labels; CERTIFICATION_BADGE still supplies the colour.
                    const certLabel = CERTIFICATION_STATUS_LABEL[certState];
                    return (
                      <TableRow key={item.user_id}>
                        {showPartnerColumn ? (
                          <TableCell
                            className="text-muted-foreground"
                            data-testid={`training-partner-${item.user_id}`}
                          >
                            {item.org_name ?? "—"}
                          </TableCell>
                        ) : null}
                        <TableCell className="font-medium">
                          {item.full_name}
                          {item.email ? (
                            <span className="block text-xs text-muted-foreground">
                              {item.email}
                            </span>
                          ) : null}
                        </TableCell>
                        {showPartnerColumn ? (
                          <TableCell data-testid={`training-role-${item.user_id}`}>
                            {ROLE_LABEL[item.role]}
                          </TableCell>
                        ) : null}
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <Badge
                              variant={cert.variant}
                              className={cert.className}
                              data-testid={`training-cert-${item.user_id}`}
                            >
                              {certLabel}
                            </Badge>
                            {item.exempted ? (
                              <Badge variant="secondary">Exempt (still owed)</Badge>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell
                          className="text-right tabular-nums"
                          data-testid={`training-extra-${item.user_id}`}
                        >
                          {/* "—" for zero in program mode (clarify #6 default);
                              PO keeps the literal count. */}
                          {showPartnerColumn && extraCount(item) === 0 ? "—" : extraCount(item)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {ledgerRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colCount} className="text-center text-muted-foreground">
                        {showPartnerColumn
                          ? "No records found."
                          : filter === "certified"
                            ? "No certified team members."
                            : filter === "uncertified"
                              ? "Everyone is certified — nothing outstanding."
                              : "No team members yet."}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
